/**
 * InviteInternalDrawer — invite a teammate within the org. Compliance
 * officer fills name + email + role + (optional) department, then picks
 * which apps to grant (permanent or temporary).
 *
 * Submits to POST /api/admin/team/invite-internal. Server creates the
 * user shell (status='invited'), writes the grants, audit-logs.
 */
import React, { useEffect, useState } from 'react';
import HipaaAckBanner from './HipaaAckBanner';
import AppGrantPicker from './AppGrantPicker';

const API = process.env.REACT_APP_API_URL || '';

const ROLES = [
  { value: 'admin',      label: 'Compliance officer',  hint: 'Org-wide access. Pair with no department.' },
  { value: 'associate',  label: 'Associate',           hint: 'Staff member. Add a department for dept-scoped visibility.' },
  { value: 'supervisor', label: 'Supervisor',          hint: 'Department supervisor. Picks up dept assignments.' },
  { value: 'lead',       label: 'Lead',                hint: 'Department lead. Picks up dept assignments.' },
  { value: 'viewer',     label: 'Internal observer',   hint: 'Read-only across granted apps.' },
];

export default function InviteInternalDrawer({ apps, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('associate');
  const [departmentId, setDepartmentId] = useState('');
  const [alsoDeptIds, setAlsoDeptIds] = useState([]); // additional departments this person oversees
  const [departments, setDepartments] = useState([]);
  const [grants, setGrants] = useState([]); // [{app_key, access_role, expires_at}]
  const [reason, setReason] = useState('');
  const [hipaaAck, setHipaaAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/departments`, { credentials: 'include' });
        const j = await r.json();
        const ds = Array.isArray(j) ? j : (j.departments || []);
        setDepartments(ds);
      } catch {}
    })();
  }, []);

  const submit = async () => {
    setError(null);
    if (!email.trim()) { setError('Email is required.'); return; }
    if (!hipaaAck && grants.length > 0) { setError('Please acknowledge the HIPAA notice before granting access.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/team/invite-internal`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(), name: name.trim() || null, role,
          department_id: departmentId || null,
          additional_department_ids: alsoDeptIds.filter(d => d !== departmentId),
          app_grants: grants,
          reason: reason || null,
          hipaa_ack: hipaaAck,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) {
        setError(j.message || 'Could not invite.');
      } else if (typeof onSaved === 'function') {
        onSaved();
      }
    } catch (e) {
      setError(e.message || 'Could not invite.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.20)', zIndex: 55 }} />
      <div role="dialog" aria-label="Invite teammate" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 92vw)',
        background: '#ffffff', borderLeft: '1px solid #cbd5e1', zIndex: 60,
        overflowY: 'auto',
      }}>
        <div style={{ position: 'sticky', top: 0, background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>Invite</div>
            <div style={{ fontSize: 16, color: '#0f172a', fontWeight: 600, marginTop: 4 }}>Add a teammate</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '4px 10px', fontSize: 12, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Close</button>
        </div>

        <div style={{ padding: '16px 20px 32px' }}>
          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoFocus
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 12 }} />

          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Name (optional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} type="text"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 12 }} />

          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 6 }}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
            {ROLES.find(r => r.value === role)?.hint}
          </div>

          {role !== 'admin' && role !== 'viewer' && (
            <>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Department (optional)</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 12 }}>
                <option value="">— no department —</option>
                {departments.map(d => {
                  const dn = typeof d === 'string' ? d : (d.name || d.id);
                  const di = typeof d === 'string' ? d : d.id;
                  return <option key={di} value={di}>{dn}</option>;
                })}
              </select>

              {departmentId && (
                <>
                  <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Also oversees (optional)</label>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                    Extra departments this person sees across the platform — for someone supervising several areas. Role and primary department stay as chosen above.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {departments.filter(d => (typeof d === 'string' ? d : d.id) !== departmentId).map(d => {
                      const dn = typeof d === 'string' ? d : (d.name || d.id);
                      const di = typeof d === 'string' ? d : d.id;
                      const on = alsoDeptIds.includes(di);
                      return (
                        <button key={di} type="button"
                          onClick={() => setAlsoDeptIds(prev => on ? prev.filter(x => x !== di) : [...prev, di])}
                          style={{
                            fontSize: 12, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                            border: on ? '1px solid #1e293b' : '1px solid #cbd5e1',
                            background: on ? '#1e293b' : '#ffffff',
                            color: on ? '#ffffff' : '#475569',
                          }}>
                          {dn}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 8, fontFamily: 'ui-monospace, monospace' }}>App access</div>
            <AppGrantPicker apps={apps} grants={grants} onChange={setGrants} defaultAccessRole={role === 'viewer' ? 'viewer' : 'participant'} />
          </div>

          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginTop: 16, marginBottom: 3 }}>Note (optional)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={240}
            placeholder="Why this person needs access (audit note)"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, resize: 'vertical' }} />

          {grants.length > 0 && (
            <HipaaAckBanner checked={hipaaAck} onChange={setHipaaAck} />
          )}

          {error && (
            <div role="alert" style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>{error}</div>
          )}

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 14px', fontSize: 13, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Cancel</button>
            <button type="button" onClick={submit} disabled={busy || !email.trim()}
              style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '8px 14px', fontSize: 13, cursor: busy ? 'wait' : 'pointer', borderRadius: 6 }}>
              {busy ? 'Inviting…' : 'Send invite'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
